import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from '../../Utils/reactIntlCompat';
import { useFormContext, Controller } from 'react-hook-form';
import {
  InputLabel,
  Select,
  FormControl,
  MenuItem,
  FormHelperText,
  Chip,
} from '@mui/material';
import { getForcedMessages } from '../../i18n';
import capitalize from '../../Helpers/StringsFormatting/capitalize';
import { useFieldRequired } from './fieldRequiredContext';

const renderValue = (selected, options) =>
  selected.map((value) => {
    const label =
      typeof options.find((option) => option.value === value)?.label ===
      'string'
        ? options.find((option) => option.value === value)?.label
        : value;

    return (
      <Chip
        key={`value-${value}`}
        label={capitalize(label)}
        sx={{ mr: '4px' }}
      />
    );
  });

const isEmptyFieldValue = (value, multiple) => {
  if (multiple) return !Array.isArray(value) || value.length === 0;

  return value === undefined || value === null || value === '';
};

const FormSelect = function FormSelect({
  intl,
  name,
  label,
  defaultValue = '',
  required = false,
  width = '100%',
  options = [],
  error = null,
  noTranslation = false,
  allUpperCase = false,
  CustomItem = null,
  noHelper = false,
  disabled = false,
  multiple = false,
}) {
  const italianTerms = getForcedMessages('it');
  const Item = CustomItem || MenuItem;

  const { control, clearErrors, getValues, setValue } = useFormContext();
  const { message = null } = error || {};
  const effectiveRequired = useFieldRequired(required);

  const nameTranslation = Object.prototype.hasOwnProperty.call(
    intl.messages,
    name?.toUpperCase()
  )
    ? intl.messages[name.toUpperCase()]
    : null;

  useEffect(() => {
    const currentValue = getValues(name);
    const resolvedDefaultValue = multiple ? defaultValue || [] : defaultValue;

    if (
      isEmptyFieldValue(currentValue, multiple) &&
      !isEmptyFieldValue(resolvedDefaultValue, multiple)
    ) {
      setValue(name, resolvedDefaultValue, {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
  }, [defaultValue, getValues, multiple, name, setValue]);

  return (
    <Controller
      control={control}
      name={name}
      rules={{
        required: {
          value: effectiveRequired,
          message: intl.messages.MANDATORY_FIELD,
        },
      }}
      defaultValue={multiple ? defaultValue || [] : defaultValue}
      render={({ field: { value, onChange } }) => (
        <FormControl
          size="medium"
          variant="outlined"
          required={effectiveRequired}
          fullWidth={width === '100%'}
          sx={{ width: width !== '100%' ? width : null }}
          error={error}
          disabled={disabled}
        >
          <InputLabel id={`${name}-label`}>
            {label || nameTranslation}
          </InputLabel>
          <Select
            id={`${name}-selectField`}
            label={label || nameTranslation}
            placeholder={`${intl.messages.INSERT} ${nameTranslation}`}
            value={multiple ? value || [] : value}
            multiple={multiple}
            renderValue={
              multiple ? (value) => renderValue(value, options) : undefined
            }
            onChange={(e) => {
              clearErrors(name);
              onChange(e);
            }}
          >
            {options
              .filter((item) => item)
              .map((item) => {
                return typeof item === 'string' ? (
                  <Item
                    key={item}
                    value={noTranslation ? item : italianTerms[item] || item}
                  >
                    {allUpperCase
                      ? (intl.messages[item] || item).toUpperCase()
                      : intl.messages[item] || item}
                  </Item>
                ) : (
                  <Item key={item?.value} {...item}>
                    {item?.label || item?.value}
                  </Item>
                );
              })}
          </Select>
          {!noHelper && (
            <FormHelperText
              sx={{
                whiteSpace: 'noWrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {message || ' '}
            </FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
};

FormSelect.propTypes = {
  name: PropTypes.string.isRequired,
  defaultValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array,
  ]),
  required: PropTypes.bool,
  width: PropTypes.string,
  intl: PropTypes.object.isRequired,
  options: PropTypes.array,
  error: PropTypes.object,
  noTranslation: PropTypes.bool,
  allUpperCase: PropTypes.bool,
  CustomItem: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.node,
    PropTypes.object,
  ]),
  noHelper: PropTypes.bool,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  multiple: PropTypes.bool,
};

export default injectIntl(FormSelect);
